/**
 * Forgot Password Modal
 * Handles password recovery flow
 */

import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { Logger } from '@/services/Logger';
import { getSiteKey } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('ForgotPassword');

/**
 * Show the forgot password modal
 */
export async function showForgotPasswordModal(): Promise<void> {
  try {
    const html = await loadOEMTemplate('/forms/auth/forgot-password.html');

    ModalSystem.show({
      title: 'Forgot Password',
      html,
      width: 'lg',
      onOpened: () => {
        setupForgotPasswordForm();
      }
    });
  } catch (error) {
    logger.error('Error loading forgot password form:', error);
    ToasterService.error('Failed to load forgot password form');
  }
}

/**
 * Setup the forgot password form submission handler
 */
function setupForgotPasswordForm(): void {
  const form = document.getElementById('forgot-password-form') as HTMLFormElement;
  if (!form) {
    logger.warn('Forgot password form not found');
    return;
  }

  // Handle "Sign in" link - just close the modal
  const signInLink = document.getElementById('forgot-signin-link');
  logger.debug('Sign in link found:', !!signInLink);
  if (signInLink) {
    signInLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      logger.debug('Sign in link clicked - closing modal');
      ModalSystem.hide();
    });
  } else {
    logger.warn('Forgot password sign-in link not found');
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    // Validate email
    if (!email) {
      ToasterService.warning('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ToasterService.warning('Please enter a valid email address');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Sending...';

    try {
      const apiBase = sessionStorage.getItem('trisys_api_endpoint') || 'https://api.trisys.co.uk';
      logger.debug('Requesting password reset', { email });

      const response = await fetch(`${apiBase}/Security/ForgottenPasswordRequest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SiteKey': getSiteKey()
        },
        body: JSON.stringify({
          EMail: email,
          WhiteLabelled: false,
          URL: 'https://api.business-software.group/portal',
          Product: 'BSG API Hub'
        })
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.Success) {
        ToasterService.error(result.ErrorMessage || 'Failed to send reset email. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
        return;
      }

      ToasterService.success('Password reset link sent to your email');
      ModalSystem.hide();
    } catch (error) {
      logger.error('Failed to send reset email:', error);
      ToasterService.error('Failed to send reset email. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
